import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import _ from 'lodash'
import styled from 'styled-components'
import { Formik, ErrorMessage } from 'formik'
import { Button, TextInput, Select } from 'components/shard'
import { Color, Size } from 'const'
import { Modal } from 'components/desktop/common/Modal'
import Alert from 'components/Alert'

import { usePayHook, useServiceHook } from 'hooks'

function PayEdit() {
  const history = useHistory()
  const [values, setValues] = useState({})
  const [modalDisplay, setModalDisplay] = useState(false)

  const {
    // id関連
    contractId,

    // creditCard関連(GET)
    creditCard,
    isCreditCardRequestingAPI,
    creditCardErrors,
    setCreditCardErrors,
    getCreditCard,

    // creditCard関連(UPDATE)
    isUpdateCCRequestingAPI,
    updateCCErrors,
    setUpdateCCErrors,
    postCreditCard,

    // validation
    validationSchema,
  } = usePayHook()

  const {
    // service関連
    service,
    isServiceRequestingAPI,
    serviceErrors,
    setServiceErrors,
    getService,
  } = useServiceHook()

  useEffect(() => {
    getCreditCard()
  }, [getCreditCard])

  useEffect(() => {
    getService()
  }, [getService])

  useEffect(() => {
    const post = async () => {
      if (_.isEmpty(values)) {
        return
      }
      await postCreditCard(values)
        .then(() => {
          setModalDisplay(true)
        })
        .catch((errorMessages) => {
          console.error('errorMessages', errorMessages)
        })
    }

    post()
  }, [values, postCreditCard])

  const close = () => {
    setModalDisplay(false)
    history.push(`/service/${contractId}`)
  }
  const link = () => {
    history.push(`/service/${contractId}`)
  }

  const hasErrors = serviceErrors.length > 0 || creditCardErrors.length > 0 || updateCCErrors.length > 0

  const Alerts = () => {
    const errorsReset = () => {
      setServiceErrors([])
      setCreditCardErrors([])
      setUpdateCCErrors([])
    }

    return (
      <Alert
        type="error"
        message="エラー"
        description={serviceErrors.concat(creditCardErrors, updateCCErrors).join('\n')}
        onClose={() => errorsReset()}
      />
    )
  }

  if (isCreditCardRequestingAPI || isServiceRequestingAPI) {
    return (
      <IsRequestingWrapper>
        <IsRequesting>現在クレジットカード情報、およびご契約情報を取得中</IsRequesting>
      </IsRequestingWrapper>
    )
  } else {
    return (
      <React.Fragment>
        <Modal
          title={_.isEmpty(creditCard) ? '登録完了' : '更新完了'}
          text={_.isEmpty(creditCard) ? 'クレジットカードの登録が完了しました' : 'クレジットカードの更新が完了しました'}
          caution="ご利用ご契約状況詳細画面に戻ります"
          display={modalDisplay}
          overlayOnClick={close}
          buttonOnClick={link}
        />
        {hasErrors && <Alerts />}
        <PayEditBlock
          creditCard={creditCard}
          service={service}
          onSubmit={(values) => {
            setValues(values)
          }}
          isUpdateCCRequestingAPI={isUpdateCCRequestingAPI}
          validationSchema={validationSchema}
        />
      </React.Fragment>
    )
  }
}

function PayEditBlock({ creditCard, service, onSubmit, isUpdateCCRequestingAPI, validationSchema }) {
  // 概要
  const info = _.isEmpty(service) ? '' : service.ninal_subscription_application
  const serviceName = _.isEmpty(service) ? '' : service.dev_service_name

  // 利用者
  const user = _.isEmpty(service) ? '' : info.user
  const userName = _.isEmpty(service) ? '' : user.lastname + user.firstname

  // 有効期限（月）の選択肢
  const expirationMonthOptions = (() => {
    const firstMonth = 1
    let array = []

    for (let i = 0; i < 12; i++) {
      array.push(firstMonth + i)
    }

    array = array.map((month) => {
      // API仕様により、Stringにしたものをoptionにする
      if (month < 10) {
        return `0${month}`
      }

      return month.toString()
    })

    return array
  })()

  // 有効期限（年）の選択肢（最長でも長いものは7年みたいなので少し多めに10年間をとる）
  const expirationYearOptions = (() => {
    const firstYear = new Date().getFullYear()
    let array = []

    for (let i = 0; i < 10; i++) {
      array.push(firstYear + i)
    }

    array = array.map((year) => {
      // API仕様により、下二桁をStringにしたものをoptionにする
      const result = year % 2000

      if (result < 10) {
        return `0${result}`
      }

      return result.toString()
    })

    return array
  })()

  return (
    <PayBlock>
      <PayTextArea>
        <PayNameLabel>ご利用者氏名</PayNameLabel>
        <PayName>{userName}</PayName>
        <PayPlanLabel>契約プラン</PayPlanLabel>
        <PayPlan>{serviceName}</PayPlan>
        <PayMethodLabel>お支払い方法</PayMethodLabel>
        <PayMethod>クレジットカード</PayMethod>
      </PayTextArea>
      <Formik
        initialValues={{
          creditCardNumber: '',
          //          creditCardName: _.isEmpty(creditCard) ? '' : creditCard.holder_name,
          //          expirationYear: _.isEmpty(creditCard) ? '' : creditCard.year,
          //          expirationMonth: _.isEmpty(creditCard) ? '' : creditCard.month,
          creditCardName: '',
          expirationYear: '',
          expirationMonth: '',
          securityCode: '',
        }}
        onSubmit={(values) => {
          onSubmit(values)
        }}
        validationSchema={validationSchema}
      >
        {({ values, handleChange, handleBlur, handleSubmit, isValid, dirty }) => (
          <form
            onSubmit={(e) => {
              handleSubmit(e)
              e.preventDefault() // useEffectでstateを更新するためにcomponentの自動更新を防ぐ
            }}
          >
            <InputLabel>カード番号</InputLabel>
            <TextInput
              type="tel"
              name="creditCardNumber"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.creditCardNumber}
              ariaLabel="creditCardNumber"
              errorMessage={<ErrorMessageWrapper name="creditCardNumber" component="div" />}
            />
            <InputLabel>クレジットカード名義</InputLabel>
            <TextInput
              type="text"
              name="creditCardName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.creditCardName}
              ariaLabel="creditCardName"
              errorMessage={<ErrorMessageWrapper name="creditCardName" component="div" />}
            />
            <InputLabel>有効期限</InputLabel>
            <InputArea>
              <Select
                name="expirationMonth"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.expirationMonth}
                ariaLabel="expirationMonth"
                errorMessage={<ErrorMessageWrapper name="expirationMonth" component="div" />}
                options={expirationMonthOptions}
              />
              <DateLabel>月</DateLabel>
              <Select
                name="expirationYear"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.expirationYear}
                ariaLabel="expirationYear"
                errorMessage={<ErrorMessageWrapper name="expirationYear" component="div" />}
                options={expirationYearOptions}
              />
              <DateLabel>年</DateLabel>
            </InputArea>
            <InputLabel>セキュリティコード</InputLabel>
            <TextInput
              type="tel"
              name="securityCode"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.securityCode}
              ariaLabel="securityCode"
              errorMessage={<ErrorMessageWrapper name="securityCode" component="div" />}
            />
            <ExplanationLabel>
              クレジットカード裏面に記載されいる3桁の数字をご入力ください。アメリカン・エキスプレスはカード表面または左端に記載されている4桁の数字をご入力ください。
            </ExplanationLabel>
            <ButtonWrapper>
              <Button type={'submit'} primary disabled={!dirty || !isValid || isUpdateCCRequestingAPI}>
                {_.isEmpty(creditCard) ? '登録' : '変更'}する
              </Button>
            </ButtonWrapper>
          </form>
        )}
      </Formik>
    </PayBlock>
  )
}

const IsRequestingWrapper = styled.div`
  border-radius: 10px;
  border: 1px solid ${Color.LINE.NORMAL};
  padding: 31px 0;
`
const IsRequesting = styled.div`
  color: ${Color.FONT.NORMAL};
  font-size: ${Size.FONT.REGULAR};
  margin-left: 24px;
`
const PayBlock = styled.div`
  padding: 20px 24px 40px 24px;
  border-radius: 10px;
  border: 1px solid ${Color.LINE.NORMAL};
`
const PayTextArea = styled.div`
  display: grid;
  grid-template-columns: 113px 1fr;
  grid-template-rows: 68px 68px 68px;
`
const PayTextLabel = `
  color: ${Color.FONT.ANNOTATION};
  font-size: ${Size.FONT.REGULAR};
  display: grid;
  align-items: center;
`
const PayText = `
  color: ${Color.FONT.NORMAL};
  font-size: ${Size.FONT.BIG};
  display: grid;
  align-items: center;
`
const PayNameLabel = styled.div`
  grid-column: 1;
  grid-row: 1;
  ${PayTextLabel}
`
const PayName = styled.div`
  grid-column: 2;
  grid-row: 1;
  ${PayText}
`
const PayPlanLabel = styled.div`
  grid-column: 1;
  grid-row: 2;
  ${PayTextLabel}
`
const PayPlan = styled.div`
  grid-column: 2;
  grid-row: 2;
  ${PayText}
`
const PayMethodLabel = styled.div`
  grid-column: 1;
  grid-row: 3;
  ${PayTextLabel}
`
const PayMethod = styled.div`
  grid-column: 2;
  grid-row: 3;
  ${PayText}
`
const InputLabel = styled.div`
  color: ${Color.FONT.NORMAL};
  font-size: ${Size.FONT.INPUT_LABEL};
  padding-top: 29px;
`
const ExplanationLabel = styled.div`
  color: ${Color.FONT.ANNOTATION};
  font-size: ${Size.FONT.REGULAR};
`
const DateLabel = styled.div`
  color: ${Color.FONT.NORMAL};
  font-size: ${Size.FONT.INPUT_LABEL};
`
const InputArea = styled.div`
  display: grid;
  grid-template-columns: 1fr 75px 1fr 70px;
  text-align: center;
  align-items: center;
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 60px;
`

const ErrorMessageWrapper = styled(ErrorMessage)`
  white-space: break-spaces;
`

PayEditBlock.propTypes = {
  creditCard: PropTypes.object,
  service: PropTypes.object,
  onSubmit: PropTypes.func,
  isUpdateCCRequestingAPI: PropTypes.bool,
}

export default PayEdit
